import { Anchor } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';

import { ErrorMessageConstants } from '../constants';

import type { UserPropertyLocationRestrictionsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

const getLocationRestrictionErrorMessage = (
  {
    duplicateExists,
    duplicateSyndicatedUrl,
    forSaleExists,
    forSaleUrlOnZillow,
    isTopListingOwner,
    paidListingExists,
    paidMultifamilyLocation,
    restrictedStreetEasy,
    restrictedOutEast,
  }: UserPropertyLocationRestrictionsResponseWebDto & { paidMultifamilyLocation: boolean },
  isMultifamily = false,
  isMultifamilyCustomer = false,
  hasActiveMultifamilyContract = false,
  isSingleFamilyEnterpriseCustomer = false,
  isPostingPathAdmin = false,
  setPropertyAlreadyPostedWarningMessage = (_data: string | Array<JSX.Element | string>) => {},
) => {
  const learnMoreElement = (
    <Anchor
      href="https://www.zillow.help/article/why-didnt-my-listing-go-live-zd30234367973523"
      target="_blank"
      rel="noopener noreferrer"
    >
      Learn more
    </Anchor>
  );
  const propertyAlreadyPostedElements = (href: string) => [
    'This property is ',
    <Anchor key="already-posted-link" href={href} target="_blank" rel="noopener noreferrer">
      already posted
    </Anchor>,
  ];
  const logErrorAndReturn = (message: Array<string | JSX.Element> | string) => {
    logError({
      error: new Error(typeof message === 'string' ? message : 'handleLocationRestrictionResponse error'),
      errorType: 'LocationRestrictionError',
      context: 'handleLocationRestrictionResponse',
    });
    return message;
  };

  setPropertyAlreadyPostedWarningMessage('');

  if (restrictedStreetEasy || restrictedOutEast) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.NYC;
    return logErrorAndReturn(message);
  }

  if (!isPostingPathAdmin && paidMultifamilyLocation && !hasActiveMultifamilyContract) {
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.MF_REQUIRED;
    return logErrorAndReturn(message);
  }

  if (paidListingExists && !isTopListingOwner) {
    return logErrorAndReturn(
      ErrorMessageConstants.PAID_LISTING_DUPE_ERROR(
        isMultifamily && isMultifamilyCustomer,
        isSingleFamilyEnterpriseCustomer,
      ),
    );
  }

  if (forSaleExists && !isMultifamily) {
    const postForSaleListingLearnMore = (
      <Anchor
        href="https://www.zillow.help/article/advertising-for-sale-and-for-rent-listings-simultaneously-zd30237388907539"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more
      </Anchor>
    );
    const message = forSaleUrlOnZillow
      ? [
          ...propertyAlreadyPostedElements(forSaleUrlOnZillow),
          ' for sale. You can post it for rent at the same time, but it may receive less visibility. ',
          postForSaleListingLearnMore,
        ]
      : [
          'This property was already posted for sale elsewhere. You can post it for rent at the same time, but it may receive less visibility. ',
          postForSaleListingLearnMore,
        ];
    return setPropertyAlreadyPostedWarningMessage(message);
  }

  if (duplicateExists) {
    if (duplicateSyndicatedUrl) {
      const message = [
        ...propertyAlreadyPostedElements(duplicateSyndicatedUrl),
        ' from another source. ',
        learnMoreElement,
      ];
      return setPropertyAlreadyPostedWarningMessage(message);
    }
    const message = ErrorMessageConstants.LOCATION_RESTRICTION.DUPLICATE.MESSAGE;
    return setPropertyAlreadyPostedWarningMessage(message);
  }

  return null;
};

export default getLocationRestrictionErrorMessage;
